import * as React from 'react';

import { Modal, ModalProps } from '@creditornot/cbt-modal';

export type ModalConfig = Omit<
  ModalProps,
  'open' | 'onDismiss' | 'onUnmount' | 'animateOnMount'
> & {
  content?: React.ReactNode;
};

type ModalEntry = {
  id: string;
  config: ModalConfig;
  open: boolean;
};

type ModalContextValue = {
  open: (id: string, config: ModalConfig) => void;
  close: (id: string) => void;
};

const ModalContext = React.createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = React.useState<ModalEntry[]>([]);

  const open = React.useCallback((id: string, config: ModalConfig) => {
    setModals((prev) => [...prev.filter((m) => m.id !== id), { id, config, open: true }]);
  }, []);

  const close = React.useCallback((id: string) => {
    setModals((prev) => prev.map((p) => (p.id === id ? { ...p, open: false } : p)));
  }, []);

  const remove = React.useCallback((id: string) => {
    setModals((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return (
    <ModalContext.Provider value={{ open, close }}>
      {children}
      {modals.map(({ id, open, config: { content, ...rest } }) => (
        <Modal
          key={id}
          ContentComponent={null}
          {...rest}
          open={open}
          animateOnMount
          onUnmount={() => remove(id)}
          onDismiss={() => close(id)}
        >
          {content}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextValue {
  const ctx = React.useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}
